/**
 * useTextDetection - Browser-based OCR for watermark detection
 *
 * Uses Tesseract.js (Google's OCR engine ported to JS) to detect text
 * overlays in job photos. This is completely FREE - runs entirely in the browser.
 *
 * @see https://github.com/naptha/tesseract.js
 *
 * Performance notes:
 * - First run: ~3-5 seconds (downloads ~15MB language model, cached in IndexedDB)
 * - Subsequent runs: ~1-2 seconds per image
 * - Model is cached between sessions
 */
import { useState, useCallback, useRef } from 'react';
import Tesseract from 'tesseract.js';

export interface TextDetectionResult {
  hasText: boolean;
  confidence: number;
  detectedText: string;
  /** Words found in specific regions - useful for watermark detection */
  bottomLeftText: string;
}

interface UseTextDetectionOptions {
  /** Minimum characters to consider "has text" (default: 10) */
  minTextLength?: number;
  /** Only scan bottom portion of image where watermarks typically are */
  scanBottomOnly?: boolean;
}

export function useTextDetection(options: UseTextDetectionOptions = {}) {
  const { minTextLength = 10, scanBottomOnly = true } = options;
  const [isDetecting, setIsDetecting] = useState(false);
  const [progress, setProgress] = useState(0);
  const workerRef = useRef<Tesseract.Worker | null>(null);

  /**
   * Initialize the Tesseract worker (lazy loaded on first use)
   * This downloads the language model once and caches it
   */
  const getWorker = useCallback(async () => {
    if (!workerRef.current) {
      workerRef.current = await Tesseract.createWorker('eng', 1, {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
          }
        },
      });
    }
    return workerRef.current;
  }, []);

  /**
   * Detect text in an image URL
   * Returns detection result with confidence and extracted text
   */
  const detectText = useCallback(async (imageUrl: string): Promise<TextDetectionResult> => {
    setIsDetecting(true);
    setProgress(0);

    try {
      const worker = await getWorker();

      // If scanning bottom only, we'll use rectangle parameter
      // This is more efficient and focused on where watermarks appear
      const recognizeOptions: Partial<Tesseract.RecognizeOptions> = {};

      if (scanBottomOnly) {
        // Load image to get dimensions for bottom region calculation
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageUrl;
        });

        // Scan bottom 30% of image where watermarks typically appear
        const bottomRegion = {
          left: 0,
          top: Math.floor(img.height * 0.7),
          width: img.width,
          height: Math.floor(img.height * 0.3),
        };
        recognizeOptions.rectangle = bottomRegion;
      }

      const { data } = await worker.recognize(imageUrl, recognizeOptions);

      const detectedText = data.text.trim();
      const hasText = detectedText.length >= minTextLength;

      // Extract text that looks like watermark patterns
      // Common patterns: dates, "Fix My Brick", addresses
      const bottomLeftText = detectedText;

      return {
        hasText,
        confidence: data.confidence,
        detectedText,
        bottomLeftText,
      };
    } catch (error) {
      console.error('Text detection failed:', error);
      return {
        hasText: false,
        confidence: 0,
        detectedText: '',
        bottomLeftText: '',
      };
    } finally {
      setIsDetecting(false);
      setProgress(0);
    }
  }, [getWorker, minTextLength, scanBottomOnly]);

  /**
   * Quick check if image likely has watermark based on common patterns
   * Faster than full OCR - looks for "Fix My Brick" pattern
   */
  const quickCheck = useCallback(async (imageUrl: string): Promise<boolean> => {
    const result = await detectText(imageUrl);

    // Check for common watermark patterns
    const patterns = [
      /fix\s*my\s*brick/i,
      /\d{4}[-/]\d{2}[-/]\d{2}/,  // Date patterns
      /\d{1,2}:\d{2}:\d{2}/,       // Time patterns
      /crew/i,
    ];

    return patterns.some(pattern => pattern.test(result.detectedText));
  }, [detectText]);

  /**
   * Cleanup worker when done (optional, but good for memory)
   */
  const terminate = useCallback(async () => {
    if (workerRef.current) {
      await workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  return {
    detectText,
    quickCheck,
    isDetecting,
    progress,
    terminate,
  };
}
